/**
 * tRPC client and typed options proxy for the Portals app.
 *
 * This module provides the `trpc` proxy — a fully-typed interface to all tRPC
 * procedures that produces TanStack Query options objects. Use it everywhere:
 *
 *   Route loaders:
 *     context.queryClient.ensureQueryData(trpc.portal.getAuthor.queryOptions({ portalId, locale }))
 *
 *   Components:
 *     useSuspenseQuery(trpc.portal.getAuthor.queryOptions({ portalId, locale }))
 *     useMutation(trpc.payments.preregisterDonation.mutationOptions())
 *
 *   Cache invalidation:
 *     queryClient.invalidateQueries({ queryKey: trpc.ngo.getProjects.queryKey() })
 *     queryClient.removeQueries({ queryKey: trpc.ngo.pathKey() })
 *
 * Transport:
 * - SSR: in-process via `callTRPCProcedure` (through a `createServerFn` boundary)
 * - Client: batched HTTP via `httpBatchStreamLink` → `/api/trpc` endpoint
 */

import { createTRPCClient, httpBatchStreamLink, httpBatchLink, splitLink, TRPCClientError } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { QueryClient } from '@tanstack/react-query';
import type { TRPCLink } from '@trpc/client';
import type { AppRouter } from './trpc/router.server';
import { trpcSSRCallFn } from './trpc-server-fn';

// ── SSR link (in-process) ────────────────────────────────────────────────────

const isServer = typeof window === 'undefined';

/**
 * Terminating tRPC link for SSR: dispatches calls through `trpcSSRCallFn`,
 * a `createServerFn` whose handler runs `callTRPCProcedure` directly in-process.
 * The server function boundary is required so that `.server.ts` imports are
 * tree-shaken from the client bundle.
 */
const ssrLink: TRPCLink<AppRouter> = () => {
	return ({ op }) => {
		return observable((observer) => {
			const type = op.type === 'subscription' ? 'query' : op.type;
			trpcSSRCallFn({ data: { path: op.path, input: op.input, type } })
				.then((result) => {
					observer.next({ result: { type: 'data', data: result } });
					observer.complete();
				})
				.catch((err: unknown) => {
					observer.error(
						err instanceof TRPCClientError
							? err
							: new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
									cause: err instanceof Error ? err : undefined,
								}),
					);
				});
		});
	};
};

// ── Unauthorized interceptor link ────────────────────────────────────────────

/**
 * Intercepts UNAUTHORIZED errors from the HTTP transport and converts them to
 * `{ unauthenticated: true }` data results. This matches the SSR behavior in
 * `trpc-server-fn.ts` and allows data hooks to check
 * `'unauthenticated' in data` without the query throwing an error boundary.
 */
const unauthorizedInterceptorLink: TRPCLink<AppRouter> = () => {
	return ({ op, next }) => {
		return observable((observer) => {
			const unsubscribe = next(op).subscribe({
				next: (value) => observer.next(value),
				complete: () => observer.complete(),
				error: (err) => {
					if (err instanceof TRPCClientError && err.data?.code === 'UNAUTHORIZED') {
						observer.next({ result: { type: 'data', data: { unauthenticated: true } } });
						observer.complete();
						return;
					}
					observer.error(err);
				},
			});
			return unsubscribe;
		});
	};
};

// ── tRPC client ──────────────────────────────────────────────────────────────

/**
 * Auth procedures that set cookies must use non-streaming transport,
 * because streaming responses flush headers before the procedure body
 * executes — so Set-Cookie headers added during execution are lost.
 */
const COOKIE_SETTING_PROCEDURES = new Set(['auth.validateMagicLink', 'auth.selectNgo', 'auth.logout']);

export const trpcClient = createTRPCClient<AppRouter>({
	links: isServer
		? [ssrLink]
		: [
				unauthorizedInterceptorLink,
				splitLink({
					condition: (op) => COOKIE_SETTING_PROCEDURES.has(op.path),
					true: httpBatchLink({ url: '/api/trpc' }),
					false: httpBatchStreamLink({ url: '/api/trpc' }),
				}),
			],
});

// ── Options proxy (singleton) ────────────────────────────────────────────────

/**
 * Placeholder QueryClient used only to satisfy `createTRPCOptionsProxy`'s
 * required parameter. It is only accessed internally by tRPC for async
 * iterable (streaming) support, which we don't use. The actual QueryClient
 * is always provided by TanStack Router's context or `useQueryClient()`.
 */
const placeholderQueryClient = new QueryClient();

/**
 * Typed tRPC proxy for generating TanStack Query options.
 *
 * This is a singleton — safe to import in route loaders, components, and hooks.
 * It does NOT need React context.
 */
export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient: placeholderQueryClient,
});
